import { useFormContext } from 'react-hook-form';
import {
  FormSwitch,
  LwFormInput,
  LwFormTextArea,
  StyledErrorText,
  StyledHelperTextWrapper,
  StyledIconWarning,
  StyledLabel,
} from 'redesign';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  FormLabel,
  Grid,
  styled,
  Typography,
} from '@mui/material';
import {
  FlexpoolManagementData,
  FlexPoolManagementFormData,
} from '../../../../../../services/flex-pool-service.types';
import { invariant } from '../../../../../../../shared/utils/utils';
import { IconCaretDown } from '../../../../../../../assets/img';

interface Props {
  state: 'create' | 'update' | 'read';
  managementData: FlexpoolManagementData[];
}

const FlexPoolForm = ({ state, managementData }: Props) => {
  return (
    <Grid container item spacing={6} xs={12}>
      <Grid item xs={6}>
        <FlexPoolFormImpl state={state} managementData={managementData} />
      </Grid>
      <Grid item xs={6}>
        <Disclaimer>
          <Typography>
            Let op; wijzigingen gemaakt aan een flexpool heeft alleen effect voor nieuw
            gepubliceerde shifts. Bestaande shifts worden niet aangepast.
          </Typography>
        </Disclaimer>
      </Grid>
    </Grid>
  );
};

const FlexPoolFormImpl = ({
  state,
  managementData,
}: Props & {
  managementData: FlexpoolManagementData[];
}) => {
  const {
    control,
    setValue,
    watch,
    trigger,
    formState: { errors },
  } = useFormContext<FlexPoolManagementFormData>();
  invariant(managementData, 'Management data is missing');

  const validateDepartments = () => {
    const departments = watch('departments');
    return Object.values(departments).some((isSelected) => isSelected);
  };

  /**
   * When a branch office is toggled, we need to ensure that all associated departments are
   * toggled ON if the branch office is selected
   */
  const onBranchOfficeToggle = (branchOfficeId: string, isChecked: boolean) => {
    const branchOffice = managementData.find((bo) => bo.id === branchOfficeId);
    if (branchOffice && branchOffice.department) {
      // Update the state of each associated department
      branchOffice.department.forEach((department) => {
        const departmentIdentifier = `departments.${department.id}` as `departments.${number}`;
        setValue(departmentIdentifier, isChecked);
      });
    }
    trigger('departments');
  };

  /**
   * When a department is toggled, we need to ensure that the branch office is toggled ON if all departments
   *  are selected and OFF if any of the departments are unselected
   */
  const onDepartmentToggle = (branchOfficeId: string) => {
    const branchOffice = managementData.find((bo) => bo.id === branchOfficeId);
    if (branchOffice && branchOffice.department) {
      // Update the branch office status based on department values
      const allDepartmentsChecked = branchOffice.department.every((department) => {
        const depIdentifier = `departments.${department.id}` as `departments.${number}`;
        return watch(depIdentifier);
      });
      const branchOfficeIdentifier = `branchOffices.${branchOfficeId}` as `branchOffices.${number}`;
      setValue(branchOfficeIdentifier, allDepartmentsChecked);
    }
    trigger('departments');
  };

  return (
    <form>
      <Grid container item spacing={2} xs={12}>
        <Grid item xs={12}>
          <LwFormInput
            name="name"
            label="Flexpool naam"
            control={control}
            rules={{ required: 'Voer een flexpoolnaam in' }}
          />
        </Grid>
        <Grid item xs={12}>
          <LwFormTextArea
            name="description"
            control={control}
            label="Omschrijving"
            disabled={state === 'read'}
          />
        </Grid>
        <Grid item xs={12}>
          <StyledLabel icon={errors.departments ? <WarningICon /> : null}>Afdelingen</StyledLabel>
          <StyledBox
            className={errors.departments ? 'error' : ''}
            data-testid="branch-offices-container"
          >
            {managementData.map((branchOffice) => {
              const branchOfficeIdentifier =
                `branchOffices.${branchOffice.id}` as `branchOffices.${number}`;
              return (
                <Grid item xs={12} key={branchOffice.id}>
                  <StyledAccordion>
                    <StyledBranchOfficeAccordionHeader
                      aria-controls="panel1-content"
                      id="panel1-header"
                      expandIcon={<IconCaretDown />}
                    >
                      <BranchOfficeName data-testid={`branch-office-name-${branchOffice.id}`}>
                        {branchOffice.name}
                      </BranchOfficeName>
                      <FormSwitch
                        name={branchOfficeIdentifier}
                        control={control}
                        onChange={(val) => onBranchOfficeToggle(branchOffice.id, val)}
                        disabled={state === 'read'}
                        data-testid={`branch-office-toggle-${branchOffice.id}`}
                      />
                    </StyledBranchOfficeAccordionHeader>
                    <AccordionDetails>
                      {branchOffice.department?.map((department) => {
                        const departmentIdentifier =
                          `departments.${department.id}` as `departments.${number}`;
                        return (
                          <ToggleWrapper key={department.id}>
                            <DepartmentName
                              htmlFor={departmentIdentifier}
                              data-testid={`department-name-${department.id}`}
                            >
                              {department.name}
                            </DepartmentName>
                            <FormSwitch
                              name={departmentIdentifier}
                              control={control}
                              onChange={() => onDepartmentToggle(branchOffice.id)}
                              disabled={state === 'read'}
                              data-testid={`department-toggle-${department.id}`}
                              rules={{ validate: validateDepartments }}
                            />
                          </ToggleWrapper>
                        );
                      })}
                    </AccordionDetails>
                  </StyledAccordion>
                </Grid>
              );
            })}
          </StyledBox>
          <StyledHelperTextWrapper className="error-text-wrapper">
            {errors.departments && (
              <StyledErrorText className={errors.departments ? 'error-state' : ''}>
                {'Selecteer minimaal één afdeling'}
              </StyledErrorText>
            )}
          </StyledHelperTextWrapper>
        </Grid>
      </Grid>
    </form>
  );
};

const Disclaimer = styled(Box)(({ theme }) => ({
  background: theme.palette.grey[100],
  padding: theme.spacing(4),
  width: '100%',
  height: '100%',
  borderRadius: theme.shape.borderRadius,
}));

const ToggleWrapper = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  paddingBottom: theme.spacing(4),
}));

const StyledAccordion = styled(Accordion)(({ theme }) => ({
  boxShadow: 'none',
  marginBottom: theme.spacing(2),

  '&:last-of-type': {
    marginBottom: 0,
  },
}));

const StyledBranchOfficeAccordionHeader = styled(AccordionSummary)(() => ({
  flexDirection: 'row-reverse',
  '& .MuiAccordionSummary-content': {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
}));

const BranchOfficeName = styled(Typography)(({ theme }) => ({
  fontWeight: theme.typography.fontWeightBold,
  paddingLeft: theme.spacing(3),
}));

const DepartmentName = styled(FormLabel)(({ theme }) => ({
  paddingLeft: theme.spacing(9),
}));

const StyledBox = styled(Box)(({ theme }) => ({
  border: `2px solid ${theme.palette.lwPrimaryAccentDark[40]}`,
  borderRadius: theme.spacing(3),
  padding: theme.spacing(2),
  '&.error': {
    borderColor: theme.palette.lwDanger[100],
    boxShadow: `0 0 0 4px ${theme.palette.lwDanger[20]}`,
  },
}));

const WarningICon = styled(StyledIconWarning)(({ theme }) => ({
  margin: theme.spacing(0, 0, 1, 1),
}));

export { FlexPoolForm };
